/**
 * Types of the credit-limits page (masterplan credit-limits-v3). The overview, config and open-invoice shapes mirror
 * the contract in `api/src/functions/credit-limits/shared/` (A2); the rest belongs to the components that stay
 * (comments, drill-down).
 */

export type OverviewScope = 'active' | 'all';
export type OverdueMark = 'red' | 'amber' | null;

export interface OverdueThresholds {
    overdueRedDays: number;
    overdueAmberDays: number;
}

export interface OpenRequestSummary {
    id: number;
    /** 'pending' | 'ready' | 'writing' while open (E11). */
    status: string;
    nextApproverEmail: string | null;
    nextApproverRole: string | null;
}

/**
 * C6, E42: a limit written into NAV (`odata`) or entered there by hand (`manual`) before the first nightly run after
 * the write (API `request-dto.ts` `effectiveLimitOf`). `writtenAt` is ISO (UTC).
 */
export interface PendingNavWrite {
    requestId: number;
    limitChf: number;
    mode: 'odata' | 'manual';
    writtenAt: string;
}

/**
 * E42: the first nightly run after the customer's youngest written / manual request shows another value than that
 * request. A hint only: the nightly value is the effective limit, no figure or tile uses this.
 */
export interface NightlyDiffers {
    requestId: number;
    /** The value of the request. */
    requestedLimitChf: number;
    mode: 'odata' | 'manual';
    writtenAt: string;
}

/** One customer row of `GET /credit-limits/overview`. Amounts in CHF except `creditInsuranceEur`. */
export interface OverviewRow {
    customerCode: string;
    customerName: string | null;
    salesManagerName: string | null;
    paymentTermCode: string | null;
    /** The effective limit (C6, E42): a pending NAV write's value until its first nightly run, else the nightly value. */
    creditLimitChf: number | null;
    /** C6: the limit of the last nightly run. Optional so older fixtures stay valid. */
    nightlyLimitChf?: number | null;
    /** false = "no limit" (effective limit <= 1): utilization and preview are null. */
    hasLimit: boolean;
    creditInsuranceEur: number | null;
    balanceChf: number;
    dueBalanceChf: number;
    salesOrdersChf: number;
    frameOrdersChf: number;
    /** balance / limit as a ratio (0.73 = 73 %). */
    utilization: number | null;
    /** (balance + open sales orders + open frame orders) / limit. */
    preview: number | null;
    overdueDays: number | null;
    overdueNetChf: number;
    overdueMark: OverdueMark;
    openRequest: OpenRequestSummary | null;
    /** C6: the written, not yet confirmed limit; null or missing = none. Optional so older fixtures stay valid. */
    pendingNavWrite?: PendingNavWrite | null;
    /** E42: the nightly run shows another value than the youngest written request; hint only. Optional like above. */
    nightlyDiffers?: NightlyDiffers | null;
}

export interface OverviewResponse {
    scope: OverviewScope;
    asOf: string;
    thresholds: OverdueThresholds;
    /** false until migration 227 exists: no request status in the rows. */
    requestsAvailable: boolean;
    total: number;
    rows: OverviewRow[];
}

export interface CreditLimitCallerRoles {
    email: string;
    finance: boolean;
    /** SalesManagerName values where the caller is the active sales manager. */
    smFor: string[];
    /** SalesManagerName values where the caller is the active BT manager. */
    btmFor: string[];
}

/** `settings` of `GET /credit-limits/config`. `navWriteLockedReason` is null for everybody but Finance. */
export interface CreditLimitSettings extends OverdueThresholds {
    /** E30: from this percentage up to 100 % a utilization or preview is «near the limit». Default 80. */
    nearLimitPct: number;
    navWriteLockedAt: string | null;
    navWriteLockedReason: string | null;
    /** C3a, Finance only: time of a kept `auth-failed:` marker (a refused NAV call whose lock was not saved); null otherwise. Optional so older fixtures stay valid. */
    navAuthFailedAt?: string | null;
    /**
     * E34: the top level of a customer without an active approver row (`DefaultTopEmail`). Always sent since B5;
     * optional in the type so older fixtures stay valid, and without it the chain preview uses `DEFAULT_TOP_EMAIL`.
     */
    defaultTopEmail?: string | null;
}

/** 'SM' | 'BTM' | 'BTM+TOP' (masterplan §4, `CreditLimitTiers.RequiredRoles`). */
export type RequiredRoles = 'SM' | 'BTM' | 'BTM+TOP';

/** One tier of `GET /credit-limits/config` (`config-repo.ts` TierConfig). */
export interface CreditLimitTier {
    tierNo: number;
    /** Upper bound in CHF, inclusive; null = open. */
    maxAmountChf: number | null;
    requiredRoles: RequiredRoles;
}

/** One approver row of `GET /credit-limits/config` (`config-repo.ts` ApproverConfig). */
export interface CreditLimitApprover {
    id: number;
    salesManagerName: string;
    smEmail: string | null;
    btmEmail: string | null;
    topEmail: string | null;
    isActive: boolean;
}

export interface CreditLimitFinanceMember {
    email: string;
    isActive: boolean;
}

export interface CreditLimitConfig {
    companyCode: string;
    /** false until migration 227: the defaults are shown. */
    schemaReady: boolean;
    tiers: CreditLimitTier[];
    approvers: CreditLimitApprover[];
    finance: CreditLimitFinanceMember[];
    settings: CreditLimitSettings;
    roles: CreditLimitCallerRoles;
    /** B5: display name per lower-case address of the rows above (`etl.EmployeeMaster`). Always sent since B5. */
    names?: Record<string, string>;
    /** B5, Finance only: active employees to pick a person from; `[]` for everybody else. */
    people?: CreditLimitPerson[];
    /** B5, Finance only: sales manager names an approver row can be created for (limit list); `[]` otherwise. */
    salesManagerOptions?: string[];
}

export interface CreditLimitPerson {
    email: string;
    name: string;
}

/** What every `PUT /credit-limits/config/*` answers: the whole configuration again, plus how many rows changed. */
export interface CreditLimitConfigWriteResult extends CreditLimitConfig {
    changed: number;
}

export interface TiersUpdateBody {
    tiers: { tierNo: number; maxAmountChf: number | null }[];
}

export interface ApproverUpdateBody {
    /** Omitted = create a new row (then `salesManagerName` and the three people are required). */
    id?: number;
    salesManagerName?: string;
    smEmail?: string;
    btmEmail?: string;
    topEmail?: string;
    isActive?: boolean;
}

export interface SettingsUpdateBody {
    overdueRedDays?: number;
    overdueAmberDays?: number;
    nearLimitPct?: number;
    defaultTopEmail?: string;
}

export interface FinanceUpdateBody {
    email: string;
    isActive: boolean;
}

// ---------------------------------------------------------------- requests (B4; contract: api/.../shared/request-dto.ts)

export type RequestStatus = 'pending' | 'ready' | 'writing' | 'written' | 'manual' | 'rejected' | 'withdrawn';
export type StepRole = 'SM' | 'BTM' | 'TOP' | 'FIN';
export type StepDecision = 'pending' | 'approved' | 'rejected';
export type RequestEventType =
    | 'submitted' | 'approved' | 'rejected' | 'overruled' | 'withdrawn' | 'ready'
    | 'nav_write_started' | 'nav_write_ok' | 'nav_write_failed' | 'manual';
export type RequestAction = 'approve' | 'reject' | 'withdraw';
/** C2/C3: NAV actions, offered to Finance by the API in `allowedActions` (never derived from a role in the browser). */
export type NavAction = 'nav_write' | 'nav_manual' | 'nav_check';

/** What the signed-in person may do with a request now (`workflow.ts` AllowedAction). */
export interface AllowedAction {
    action: RequestAction | NavAction;
    /** approve: the person decides for the step's approver (E5): «on behalf». */
    onBehalf: boolean;
    commentRequired: boolean;
}

export interface RequestStepDto {
    stepNo: number;
    role: StepRole;
    approverEmail: string;
    decision: StepDecision;
    decidedBy: string | null;
    decidedAt: string | null;
    comment: string | null;
    onBehalf: boolean;
}

export interface RequestEventDto {
    id: number;
    type: RequestEventType;
    actorEmail: string;
    comment: string | null;
    createdAt: string;
}

export interface RequestNavDto {
    mode: 'odata' | 'manual' | null;
    writtenBy: string | null;
    writtenAt: string | null;
    readBackChf: number | null;
    error: string | null;
    dwhLimitChf: number | null;
    confirmedByNightlyRun: boolean;
}

/** One request of `GET /credit-limits/requests` and the answer of the create and action calls. */
export interface CreditLimitRequestDto {
    id: number;
    companyCode: string;
    customerCode: string;
    customerName: string | null;
    salesManagerName: string | null;
    /** The DWH limit of the day before the request. */
    currentLimitChf: number;
    requestedLimitChf: number;
    reason: string;
    tierNo: number;
    status: RequestStatus;
    isOpen: boolean;
    requestedBy: string;
    requestedAt: string;
    /** Only while `pending`. A FIN step names one Finance person; any other may decide too. */
    nextApproverEmail: string | null;
    nextApproverRole: StepRole | null;
    readyAt: string | null;
    closedAt: string | null;
    nav: RequestNavDto;
    steps: RequestStepDto[];
    events: RequestEventDto[];
    allowedActions: AllowedAction[];
}

export interface RequestListDto {
    requests: CreditLimitRequestDto[];
    total: number;
}

export interface CreateRequestBody {
    customerCode: string;
    requestedLimitChf: number;
    reason: string;
}

export interface RequestActionBody {
    action: RequestAction;
    comment: string | null;
    onBehalf: boolean;
}

/**
 * The overview as the page uses it: the response of `GET /credit-limits/overview` plus the near-limit threshold of
 * `GET /credit-limits/config` (E30), which `useCreditLimits` adds (80 until the config has loaded or when it failed).
 */
export interface OverviewData extends OverviewResponse {
    nearLimitPct: number;
}

/** E30: the near-limit threshold in percent while the config has not said otherwise (the setting is not seeded). */
export const DEFAULT_NEAR_LIMIT_PCT = 80;

/** E30 band of a ratio: over (> 100 %, red), near (NearLimitPct to 100 %, amber), ok (below, green), none (no limit, grey). */
export type LimitBand = 'over' | 'near' | 'ok' | 'none';

export interface OpenInvoice {
    entryNo: number;
    documentNr: string | null;
    documentType: string | null;
    postingDate: string | null;
    dueDate: string | null;
    currencyCode: string | null;
    restChf: number;
    daysOverdue: number | null;
}

export interface OpenInvoicesResponse {
    customerCode: string;
    asOf: string;
    totalRestChf: number;
    items: OpenInvoice[];
}

export interface CreditLimitComment {
    Id: number;
    CustomerCode: string;
    CommentText: string;
    CommentType: 'note' | 'action' | 'decision';
    IsActive: boolean;
    CreatedBy: string;
    CreatedAt: string;
    UpdatedBy: string | null;
    UpdatedAt: string | null;
}

export interface SalesTransaction {
    DocumentNo: string;
    DocumentType: number;
    PostingDate: string;
    Description: string | null;
    CurrencyCode: string | null;
    NetSalesLCY: number;
}

export interface OpenOrder {
    DocumentCode: string;
    DocumentID: string;
    DocumentType: string;
    CreateDate: string;
    ExpectedDeliveryDate: string | null;
    ProductID: string;
    ProductName: string | null;
    OutstandingQuantity: number;
    UnitPriceEUR: number;
    OutstandingNetSalesEUR: number;
    SalesUnit: string;
}

export type DrillDownType = 'salesTransactions' | 'openOrders' | 'openSalesOrders' | 'openFrameOrders';

export type OpenOrderTypeFilter = 'sales' | 'frame';
